import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Breadcrumb,
    Button,
    Card,
    Descriptions,
    Divider,
    message,
    Modal,
    Popconfirm,
    Space,
    Spin,
    Table,
    Tag,
    Tree,
    Typography
} from 'antd';
import {
    ApartmentOutlined,
    DeleteOutlined,
    EditOutlined,
    NodeIndexOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import {
    buildMenuTree,
    createMenu,
    deleteMenu,
    deleteMenuBatch,
  getRuntimeUnregisteredMenus,
    getMenuRegistry,
    getMenus,
    reorderMenus,
    updateMenu
} from '../api/menuApi';
import {invalidateAllMenuCache} from '../api/meApi';
import MenuCreateEditModal from './MenuCreateEditModal';
import {useMenuContext} from '../contexts/MenuContext';
import ManagementHeader from './ManagementHeader';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';

const { Text } = Typography;

const MenuManagement = () => {
  const [allMenus, setAllMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [reordering, setReordering] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [parentForCreate, setParentForCreate] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [rteRegistry, setRteRegistry] = useState([]);
  const [runtimeCandidates, setRuntimeCandidates] = useState([]);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [modalInitialValues, setModalInitialValues] = useState(null);
  const menuCtx = useMenuContext();

  // 권한 훅
  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getMenus);
  const canCreate = canCall(createMenu);
  const canUpdate = canCall(updateMenu);
  const canDeleteSingle = canCall(deleteMenu);
  const canDeleteMulti = canCall(deleteMenuBatch);
  const canReorder = canCall(reorderMenus);
  const canFetchRuntime = canCall(getRuntimeUnregisteredMenus);

  const fetchMenus = useCallback(async () => {
    setLoading(true); setTreeLoading(true);
    try {
      if (permLoading) return;
      if (!canRead) { setAllMenus([]); message.warning('메뉴 목록 조회 권한이 없습니다.'); return; }
      const res = await getMenus();
      if (res && Array.isArray(res.data)) {
        setAllMenus(res.data);
        if (selectedKey) {
          const found = res.data.find(m => m.menuId === selectedKey);
          setSelectedMenu(found || null);
          if (!found) setSelectedKey(null);
        }
      } else {
        setAllMenus([]);
        message.warning('메뉴 응답 형식이 올바르지 않습니다.');
      }
    } catch (e) { /* 에러 토스트는 공통 인터셉터에서 처리 */ }
    finally { setLoading(false); setTreeLoading(false); }
  }, [permLoading, canRead, selectedKey]);

  useEffect(() => { fetchMenus(); }, [fetchMenus]);

  const fetchRuntimeCandidates = useCallback(async () => {
    if (permLoading) return;
    if (!canFetchRuntime) {
      setRuntimeCandidates([]);
      return;
    }
    setRuntimeLoading(true);
    try {
      const res = await getRuntimeUnregisteredMenus();
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setRuntimeCandidates(list || []);
    } catch (e) {
      setRuntimeCandidates([]);
    } finally {
      setRuntimeLoading(false);
    }
  }, [permLoading, canFetchRuntime]);

  useEffect(() => { fetchRuntimeCandidates(); }, [fetchRuntimeCandidates]);

  // 라우트키 레지스트리 로드 (매핑 태그 표시용)
  useEffect(() => {
    const loadRegistry = async () => {
      try {
        // setRegistryLoading(true);
        const res = await getMenuRegistry();
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setRteRegistry(list || []);
      } catch (e) {
        setRteRegistry([]);
      } finally {
        // setRegistryLoading(false);
      }
    };
    loadRegistry();
  }, []);

  const treeData = useMemo(() => buildMenuTree(allMenus), [allMenus]);
  const childMenus = useMemo(() => allMenus.filter(m => m.parentMenuId === (selectedMenu?.menuId || null)), [allMenus, selectedMenu]);

  const runtimeEmptyText = useMemo(() => {
    if (permLoading) return '권한 확인 중...';
    if (!canFetchRuntime) return '조회 권한이 없습니다.';
    return '미등록 런타임 메뉴가 없습니다.';
  }, [permLoading, canFetchRuntime]);

  // 선택 메뉴의 조상 경로(Breadcrumb) 계산
  const breadcrumbChain = useMemo(() => {
    if (!selectedMenu) return [];
    const chain = [];
    let cur = selectedMenu;
    const safeGuard = new Set();
    while (cur) {
      chain.push(cur);
      if (!cur.parentMenuId) break;
      if (safeGuard.has(cur.parentMenuId)) break; // 순환 방지
      safeGuard.add(cur.parentMenuId);
      const parentId = cur.parentMenuId;
      cur = allMenus.find(m => m.menuId === parentId);
    }
    return chain.reverse();
  }, [selectedMenu, allMenus]);

  const columns = [
    { title: '메뉴명', dataIndex: 'menuNm', key: 'menuNm', 
      sorter: (a, b) => (a.menuNm || '').localeCompare(b.menuNm || ''),
      render: (t, r) => <Button type="link" onClick={() => { setSelectedKey(r.menuId); setSelectedMenu(r); }}>{t}</Button> 
    },
    { title: '라우트키', dataIndex: 'rteKey', key: 'rteKey', width: 180, 
      sorter: (a, b) => (a.rteKey || '').localeCompare(b.rteKey || ''),
      render: (v) => v ? (
        <Space size={4}>
          <Text>{v}</Text>
          {rteRegistry.includes(v) ? <Tag color="green">등록</Tag> : <Tag color="volcano">미등록</Tag>}
        </Space>
      ) : <Tag>-</Tag> 
    },
    { title: '등록자', dataIndex: 'rgtr', key: 'rgtr', width: 120, sorter: (a, b) => (a.rgtr || '').localeCompare(b.rgtr || '') },
    { title: '등록일시', dataIndex: 'regDt', key: 'regDt', width: 170, sorter: (a, b) => (a.regDt || '').localeCompare(b.regDt || ''), render: (v) => formatDateTime(v) },
    { title: '수정자', dataIndex: 'mdfr', key: 'mdfr', width: 120, sorter: (a, b) => (a.mdfr || '').localeCompare(b.mdfr || '') },
    { title: '수정일시', dataIndex: 'mdfcnDt', key: 'mdfcnDt', width: 170, sorter: (a, b) => (a.mdfcnDt || '').localeCompare(b.mdfcnDt || ''), render: (v) => formatDateTime(v) },
    { title: '사용', dataIndex: 'useYn', key: 'useYn', width: 80, sorter: (a, b) => (a.useYn || '').localeCompare(b.useYn || ''), render: (v) => <Tag color={v === 'Y' ? 'green' : 'volcano'}>{v || '-'}</Tag> },
    { title: '작업', key: 'actions', width: 160, render: (_, record) => (
      <Space>
        <PermissionButton size="small" icon={<PlusOutlined />} onClick={() => handleOpenCreateChild(record)} require={createMenu}>하위</PermissionButton>
        <PermissionButton size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)} require={updateMenu}>수정</PermissionButton>
        <Popconfirm title="삭제" description={`'${record.menuNm}' 메뉴를 삭제하시겠습니까?`} okText="삭제" cancelText="취소" onConfirm={() => handleDelete(record)}>
          <PermissionButton size="small" danger icon={<DeleteOutlined />} require={deleteMenu}>삭제</PermissionButton>
        </Popconfirm>
      </Space>
    ) }
  ];

  const runtimeColumns = [
    {
      title: '권장 메뉴명',
      dataIndex: 'suggestedMenuNm',
      key: 'suggestedMenuNm',
      render: (value, record) => value || record.rteKey
    },
    {
      title: '라우트키',
      dataIndex: 'rteKey',
      key: 'rteKey',
      render: (val) => <Tag color="blue">{val}</Tag>
    },
    {
      title: '기본 경로',
      dataIndex: 'defaultPath',
      key: 'defaultPath',
      render: (val) => val ? <Tag color="geekblue">{val}</Tag> : <Text type="secondary">-</Text>
    },
    {
      title: '설명',
      dataIndex: 'description',
      key: 'description',
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">-</Text>
    },
    {
      title: '작업',
      key: 'runtime-actions',
      width: 140,
      render: (_, record) => (
        <PermissionButton size="small" type="primary" icon={<PlusOutlined />} onClick={() => handleCreateFromCandidate(record)} require={createMenu}>
          메뉴 생성
        </PermissionButton>
      )
    }
  ];

  const handleTreeSelect = (_keys, info) => {
    const key = info.node?.key; setSelectedKey(key); const m = allMenus.find(x => x.menuId === key); setSelectedMenu(m || null);
  };

  const handleOpenCreateRoot = () => {
    if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); return; }
    setModalMode('create');
    setParentForCreate(null);
    setEditRecord(null);
    setModalInitialValues(null);
    setModalOpen(true);
  };
  const handleOpenCreateChild = (parent = selectedMenu) => {
    if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); return; }
    setModalMode('create');
    setParentForCreate(parent);
    setEditRecord(null);
    setModalInitialValues(null);
    setModalOpen(true);
  };
  const handleOpenEdit = (record = selectedMenu) => {
    if (!record) { message.warning('수정할 메뉴를 선택하세요.'); return; }
    if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; }
    setModalMode('edit');
    setParentForCreate(null);
    setEditRecord(record);
    setModalInitialValues(null);
    setModalOpen(true);
  };

  const handleDelete = async (record = selectedMenu) => {
    if (!record) { message.warning('삭제할 메뉴를 선택하세요.'); return; }
    if (permLoading || !canDeleteSingle) { message.warning('삭제 권한이 없습니다.'); return; }
    try { await deleteMenu(record.menuId); message.success('삭제되었습니다.'); await fetchMenus(); }
    catch (e) { /* 에러 토스트는 공통 인터셉터에서 처리 */ }
  };

  const handleBatchDelete = async () => {
    if (!checkedKeys.length) { message.warning('삭제할 메뉴를 선택(체크)하세요.'); return; }
    if (permLoading || !canDeleteMulti) { message.warning('일괄 삭제 권한이 없습니다.'); return; }
    Modal.confirm({
      title: '선택 삭제',
      content: `선택된 ${checkedKeys.length}개 메뉴를 삭제하시겠습니까?`,
      okText: '삭제', okType: 'danger', cancelText: '취소',
      onOk: async () => {
        try { await deleteMenuBatch(checkedKeys); message.success('선택 메뉴 삭제 완료'); setCheckedKeys([]); await fetchMenus(); }
        catch (e) { /* 에러 토스트는 공통 인터셉터에서 처리 */ }
      }
    });
  };

  const handleModalSubmit = async (payload) => {
    try {
      if (modalMode === 'create') {
        if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); return; }
        await createMenu(payload); message.success('메뉴가 생성되었습니다.');
      } else if (modalMode === 'edit' && (editRecord || selectedMenu)) {
        if (permLoading || !canUpdate) { message.warning('수정 권한이 없습니다.'); return; }
        const targetId = editRecord?.menuId || selectedMenu.menuId;
        await updateMenu(targetId, payload); message.success('메뉴가 수정되었습니다.');
      }
      setModalOpen(false);
      setEditRecord(null);
      setModalInitialValues(null);
      await fetchMenus();
  await fetchRuntimeCandidates();
      // 전체 앱 메뉴 컨텍스트도 즉시 갱신 (라우트키/경로 변경 반영)
      menuCtx?.refreshMenus?.();
    } catch (e) { /* 에러 토스트는 공통 인터셉터에서 처리 */ }
  };

  const handleCreateFromCandidate = (candidate) => {
    if (!candidate) return;
    if (permLoading || !canCreate) { message.warning('생성 권한이 없습니다.'); return; }
    const parent = selectedMenu || null;
    setModalMode('create');
    setParentForCreate(parent);
    setEditRecord(null);
    const nextSort = parent
      ? allMenus.filter(m => m.parentMenuId === parent.menuId).length
      : allMenus.filter(m => !m.parentMenuId).length;
    setModalInitialValues({
      menuNm: candidate.suggestedMenuNm || candidate.rteKey,
      path: candidate.defaultPath || '',
      rteKey: candidate.rteKey,
      sortSeq: nextSort,
      useYn: true,
      description: candidate.description || ''
    });
    setModalOpen(true);
  };

  const onDrop = async (info) => {
    if (reordering) return;
    if (permLoading || !canReorder) { message.warning('재정렬 권한이 없습니다.'); return; }
    try {
      setReordering(true);
      const dragId = info.dragNode?.key;
      const targetId = info.node?.key;
      if (!dragId || !targetId || dragId === targetId) return;
      const dropToGap = info.dropToGap;
      const targetItem = allMenus.find(m => m.menuId === targetId);
      const dragItem = allMenus.find(m => m.menuId === dragId);
      if (!targetItem || !dragItem) return;
      const originalParent = dragItem.parentMenuId || null;
      let newParent = null;
      let newOrderSiblings = [];
      if (!dropToGap) {
        newParent = targetItem.menuId;
        const newParentChildren = allMenus.filter(m => (m.parentMenuId || null) === newParent);
        newOrderSiblings = [...newParentChildren.map(c => c.menuId), dragItem.menuId];
      } else {
        newParent = targetItem.parentMenuId || null;
        const siblings = allMenus.filter(m => (m.parentMenuId || null) === newParent && m.menuId !== dragItem.menuId).sort((a,b)=> (a.sortSeq ?? 0) - (b.sortSeq ?? 0));
        const dropPosArr = info.node.pos.split('-');
        const targetPos = parseInt(dropPosArr[dropPosArr.length -1],10);
        const relative = info.dropPosition - targetPos;
        const insertIndex = siblings.findIndex(s => s.menuId === targetItem.menuId) + (relative > 0 ? 1 : 0);
        const before = siblings.slice(0, insertIndex).map(s=>s.menuId);
        const after = siblings.slice(insertIndex).map(s=>s.menuId);
        newOrderSiblings = [...before, dragItem.menuId, ...after];
      }
      if (originalParent === newParent && newOrderSiblings.length === 0) return;
      const affectedParentIds = new Set([originalParent, newParent]);
      const commands = [];
      affectedParentIds.forEach(parentId => {
        if (parentId === newParent) {
          newOrderSiblings.forEach((id, idx) => { commands.push({ menuId: id, parentMenuId: newParent, sortSeq: idx }); });
        } else if (parentId !== newParent) {
          const oldSiblings = allMenus.filter(m => (m.parentMenuId || null) === parentId && m.menuId !== dragItem.menuId).sort((a,b)=> (a.sortSeq ?? 0) - (b.sortSeq ?? 0));
          oldSiblings.forEach((m, idx) => { commands.push({ menuId: m.menuId, parentMenuId: parentId, sortSeq: idx }); });
        }
      });
      await reorderMenus(commands);
      message.success('메뉴 순서가 변경되었습니다.');
      await fetchMenus();
      menuCtx?.refreshMenus?.();
    } catch (e) { /* 에러 토스트는 공통 인터셉터에서 처리 */ console.error('재정렬 실패', e); }
    finally { setReordering(false); }
  };

  const handleInvalidateAllMenuCache = async () => {
    try {
      await invalidateAllMenuCache();
      message.success('전체 사용자 메뉴 캐시가 초기화되었습니다.');
      // 현재 사용자도 메뉴를 새로고침
      menuCtx?.refreshMenus?.();
    } catch (e) {
      message.error('메뉴 캐시 초기화 실패');
      console.error('메뉴 캐시 초기화 실패:', e);
    }
  };

  const HeaderActions = (
    <Space wrap>
      <PermissionButton type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateRoot} require={createMenu}>루트메뉴</PermissionButton>
      <PermissionButton icon={<ApartmentOutlined />} onClick={() => handleOpenCreateChild()} disabled={!selectedMenu} require={createMenu}>하위생성</PermissionButton>
      <PermissionButton icon={<EditOutlined />} onClick={() => handleOpenEdit()} disabled={!selectedMenu} require={updateMenu}>수정</PermissionButton>
      <Popconfirm title="삭제" description="선택된(현재) 메뉴를 삭제하시겠습니까?" okText="삭제" cancelText="취소" onConfirm={() => handleDelete()} disabled={!selectedMenu} >
        <PermissionButton danger icon={<DeleteOutlined />} disabled={!selectedMenu} require={deleteMenu}>삭제</PermissionButton>
      </Popconfirm>
      <PermissionButton danger icon={<DeleteOutlined />} onClick={handleBatchDelete} disabled={!checkedKeys.length} require={deleteMenuBatch}>선택삭제</PermissionButton>
      <PermissionButton icon={<ReloadOutlined />} onClick={fetchMenus} require={getMenus}>새로고침</PermissionButton>
      <Button icon={<ReloadOutlined />} onClick={handleInvalidateAllMenuCache}>전체 사용자 캐시 갱신</Button>
    </Space>
  );

  const allMenuIds = useMemo(() => allMenus.map(m => m.menuId), [allMenus]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  useEffect(() => { setExpandedKeys(allMenuIds); }, [allMenuIds]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ManagementHeader icon={<NodeIndexOutlined />} title="메뉴 관리" actions={HeaderActions} description="시스템 메뉴(루트 및 하위 구조)를 생성/수정/삭제할 수 있습니다." />
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Card title="메뉴 트리" style={{ flex: '1 1 300px', minWidth: 280, maxWidth: 400 }} styles={{ body: { maxHeight: 600, overflow: 'auto' } }} extra={treeLoading && <Spin size="small" /> }>
          <Tree treeData={treeData} showLine blockNode selectable checkable draggable onDrop={onDrop} onSelect={handleTreeSelect} selectedKeys={selectedKey ? [selectedKey] : []} checkedKeys={checkedKeys} onCheck={(k) => setCheckedKeys(k.checked || k)} fieldNames={{ title: 'title', key: 'key', children: 'children' }} expandedKeys={expandedKeys} onExpand={setExpandedKeys} />
        </Card>
        <Card
          title={selectedMenu ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {breadcrumbChain.length > 0 && (
                <Breadcrumb style={{ fontSize: 12 }}>
                  {breadcrumbChain.map((m, idx) => (
                    <Breadcrumb.Item key={m.menuId || idx}>{m.menuNm}</Breadcrumb.Item>
                  ))}
                </Breadcrumb>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Text strong style={{ fontSize: 16 }}>{selectedMenu.menuNm}</Text>
                <Tag color={selectedMenu.useYn === 'Y' ? 'green' : 'volcano'}>{selectedMenu.useYn === 'Y' ? '사용' : '미사용'}</Tag>
              </div>
            </div>
          ) : '메뉴 상세'}
          style={{ flex: '3 1 500px', minWidth: 400, overflowX: 'auto' }}
          styles={{ body: { padding: 16 } }}
          extra={selectedMenu && (
            <Space size={8}>
              <PermissionButton size="small" icon={<PlusOutlined />} onClick={() => handleOpenCreateChild()} require={createMenu}>하위생성</PermissionButton>
              <PermissionButton size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit()} require={updateMenu}>수정</PermissionButton>
            </Space>
          )}
        >
          {selectedMenu ? (
            <div>
              <Descriptions size="small" column={{ xs: 1, sm: 1, md: 2, lg: 3 }} style={{ marginBottom: 8 }}>
                <Descriptions.Item label="메뉴 ID">{selectedMenu.menuId}</Descriptions.Item>
                <Descriptions.Item label="부모메뉴">
                  {(() => { const p = allMenus.find(m => m.menuId === selectedMenu.parentMenuId); return p ? `${p.menuNm} (${p.menuId})` : '-'; })()}
                </Descriptions.Item>
                <Descriptions.Item label="정렬순서">{selectedMenu.sortSeq ?? '-'}</Descriptions.Item>
                <Descriptions.Item label="라우트키(rteKey)">{selectedMenu.rteKey || '-'} {selectedMenu.rteKey && (rteRegistry.includes(selectedMenu.rteKey) ? <Tag color="green">등록</Tag> : <Tag color="volcano">미등록</Tag>)}</Descriptions.Item>
                <Descriptions.Item label="경로(path)">{selectedMenu.path || '-'}</Descriptions.Item>
                <Descriptions.Item label="사용여부"><Tag color={selectedMenu.useYn === 'Y' ? 'green' : 'volcano'}>{selectedMenu.useYn || '-'}</Tag></Descriptions.Item>
                <Descriptions.Item label="아이콘"><Text code>{selectedMenu.icon || '-'}</Text></Descriptions.Item>
                <Descriptions.Item label="등록정보">
                  {(selectedMenu.rgtr || '-') + ' / ' + (formatDateTime(selectedMenu.regDt) || '-')}
                </Descriptions.Item>
                <Descriptions.Item label="수정정보">
                  {(selectedMenu.mdfr || '-') + ' / ' + (formatDateTime(selectedMenu.mdfcnDt) || '-')}
                </Descriptions.Item>
                <Descriptions.Item label="설명" span={3}>{selectedMenu.description || '-'}</Descriptions.Item>
              </Descriptions>

              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text strong>하위 메뉴</Text>
                <Text type="secondary">총 {childMenus.length}개</Text>
              </div>
            </div>
          ) : (
            <Text type="secondary">왼쪽 트리에서 메뉴를 선택하면 상세와 하위 목록이 표시됩니다.</Text>
          )}
          <Table size="small" rowKey="menuId" columns={columns} dataSource={childMenus} pagination={false} loading={loading} locale={{ emptyText: selectedMenu ? '하위 메뉴가 없습니다.' : '메뉴를 선택하세요.' }} />
        </Card>
      </div>

      <Card
        title="런타임 미등록 메뉴"
        style={{ width: '100%' }}
        extra={(
          <Space size={12} wrap>
            <Text type="secondary" style={{ fontSize: 12 }}>선택된 메뉴가 있을 경우 해당 메뉴의 하위로 생성됩니다.</Text>
            <PermissionButton
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchRuntimeCandidates}
              loading={runtimeLoading}
              require={getRuntimeUnregisteredMenus}
            >
              새로고침
            </PermissionButton>
          </Space>
        )}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          애플리케이션에 존재하지만 현재 DB에 등록되지 않은 메뉴입니다. 필요한 항목을 선택하여 바로 생성할 수 있습니다.
        </Text>
        <Table
          size="small"
          rowKey={(record) => record.rteKey}
          columns={runtimeColumns}
          dataSource={runtimeCandidates}
          pagination={false}
          loading={runtimeLoading}
          locale={{ emptyText: runtimeEmptyText }}
        />
      </Card>

      <MenuCreateEditModal
        open={modalOpen}
        mode={modalMode}
        loading={false}
        parentMenu={parentForCreate}
        record={modalMode === 'edit' ? editRecord : null}
        initialValues={modalMode === 'create' ? modalInitialValues : null}
        onCancel={() => { setModalOpen(false); setEditRecord(null); setModalInitialValues(null); }}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
};

const formatDateTime = (val) => { if (!val) return ''; const d = new Date(val); if (isNaN(d.getTime())) return val; return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };

export default MenuManagement;
